import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Carousel from "antd/lib/carousel";
import ReactGA from "react-ga4";

import { SimilarAdCard } from "../../components/SimilarAdCard";
import { Ads, parseSummaryData } from "../../services/api";
import { getSummaryConfig } from "../../dataConfig";
import { selectFavoriteIds } from "../../routes/Favorite/favorite.ducks";
import { getIconUrl } from "../../services/asset";
import getWidthContext from "../../HOCs/getWidthContext";
import {
  CategoryName,
  ModuleName
} from "../../models";
import { SimilarModelPair } from "../../typings/interfaces";

import styles from "./availableAdsWidget.scss";

interface AvailableAdsWidgetProps {
  makeId: string;
  modelId: string;
  // Extra (make, model) pairs configured as variants on the info page —
  // their ads are surfaced alongside the primary model's.
  variants?: SimilarModelPair[];
  // Injected by the getWidthContext HOC — app-wide viewport width.
  width?: number;
}

const QUANTITY = 16;

// Below this width we treat the screen as mobile and cap the carousel at
// MOBILE_MAX ads. Matches the smallest breakpoint in `carouselResponsive`.
const MOBILE_BREAKPOINT = 700;
const MOBILE_MAX = 8;

// Matches the responsive settings SimilarAdsSection uses on ad-details so
// the carousel behaves identically across the site.
const carouselResponsive = [
  { breakpoint: 1900, settings: { slidesToShow: 3, slidesToScroll: 3 } },
  { breakpoint: 1600, settings: { slidesToShow: 4, slidesToScroll: 4 } },
  { breakpoint: 1400, settings: { slidesToShow: 3, slidesToScroll: 3 } },
  { breakpoint: 1000, settings: { slidesToShow: 2, slidesToScroll: 2 } },
  { breakpoint: MOBILE_BREAKPOINT, settings: { slidesToShow: 1, slidesToScroll: 1 } }
];

const AvailableAdsWidget = (props: AvailableAdsWidgetProps) => {
  const { makeId, modelId, variants, width } = props;
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Primary modelId + all variant modelIds, de-duplicated. Empty variants
  // (before BE lands the field) simply fall back to just the primary.
  const modelIds = useMemo(() => {
    const ids = [modelId];
    if (Array.isArray(variants)) {
      variants.forEach(v => {
        if (v && v.modelId && ids.indexOf(v.modelId) === -1) {
          ids.push(v.modelId);
        }
      });
    }
    return ids.filter(Boolean);
  }, [modelId, variants]);

  const favoriteIds = useSelector(selectFavoriteIds);

  useEffect(() => {
    if (!makeId || modelIds.length === 0) return;

    let cancelled = false;
    const fetchAds = async () => {
      setLoading(true);
      try {
        // Ads.list (the /aditem search endpoint) is what SearchPage uses and
        // honours include*Ids filters reliably. widget-search was returning the
        // full unfiltered list here so ads for other makes/models leaked in.
        // Sending includeMakeIds alongside includeModelIds made the BE relax the
        // model filter and mix in other models of the same make.
        const result = await Ads.list({
          module: ModuleName.vehicles,
          category: CategoryName.car,
          includeModelIds: modelIds,
          itemPerPage: QUANTITY
        });
        const rawAds = (result && result.data) || [];
        if (rawAds.length === 0) {
          if (!cancelled) setAds([]);
          return;
        }
        const summaryConfig = await getSummaryConfig(rawAds[0].module);
        const parsed = rawAds.map((ad: any) =>
          parseSummaryData(summaryConfig, ad, favoriteIds)
        );
        if (!cancelled) setAds(parsed);
      } catch (e) {
        if (!cancelled) setAds([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAds();
    return () => {
      cancelled = true;
    };
  }, [makeId, modelIds]);

  if (loading && ads.length === 0) {
    return null;
  }

  const isMobile = typeof width === "number" && width < MOBILE_BREAKPOINT;
  const visibleAds = isMobile ? ads.slice(0, MOBILE_MAX) : ads;

  const handleAdClick = () => {
    ReactGA.event({
      category: "Informacije o modelu",
      action: "Click",
      label: "Otvoren oglas sa stranice"
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <img
          className={styles.headerIcon}
          src={getIconUrl("megaphone.svg")}
          alt=""
        />
        <div className={styles.title}>Dostupni oglasi za ovaj model</div>
      </div>
      {visibleAds.length > 0 ? (
        <Carousel
          swipe
          draggable
          slidesToShow={4}
          slidesToScroll={4}
          infinite={false}
          dots
          // @ts-ignore
          customPaging={() => <div className={styles.dot} />}
          responsive={carouselResponsive}
          className={styles.carousel}
        >
          {visibleAds.map((ad: any) => (
            <SimilarAdCard
              key={ad.id}
              className={styles.adCard}
              ad={ad}
              adLists={{ similar: { data: [ad] } }}
              category={CategoryName.car}
              flag="MakeModelInfoAvailableAds"
              eventLabel="Model info page – dostupni oglasi"
              onClick={handleAdClick}
            />
          ))}
        </Carousel>
      ) : (
        <div className={styles.empty}>
          Trenutno nema dostupnih oglasa za ovaj model.
        </div>
      )}
    </div>
  );
};

export default getWidthContext(AvailableAdsWidget);



// WEBPACK FOOTER //
// ./src/containers/MakeModelInfoPage/AvailableAdsWidget.tsx